import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { SharedModule } from "../../../../shared/shared.module";
import { OF_LIST_DATA } from "../../models/data.model";
import { Router } from "@angular/router";
import { OF } from "../../../of/models/of.model";
import { Widget, WidgetSubType } from "../../models/dashboard.model";
import { OfEtat } from "../../../of/enums/etat-of.enum";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  OfListDialogComponent,
  OfListDialogData,
} from "./of-list-dialog.component";

@Component({
  selector: "vl-widget-of-list",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    ChipsComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-of-list.component.html",
  styleUrl: "./widget-of-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetOfListComponent {
  data = input<OF_LIST_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  subType = signal<WidgetSubType>(WidgetSubType.OF_ATTENTE_REINTEGRATION);
  router = inject(Router);
  articlesInfosFacade = inject(ArticlesInfosFacadeService);
  private dialogs = inject(DialogsService);
  widgetSubType = WidgetSubType;

  private etatForSubType: Record<string, OfEtat> = {
    [WidgetSubType.OF_ATTENTE_PROD]: OfEtat.EN_ATTENTE_PROD,
    [WidgetSubType.OF_EN_PROD]: OfEtat.EN_PROD,
    [WidgetSubType.OF_ATTENTE_REINTEGRATION]: OfEtat.EN_ATTENTE_REINTE_PDC,
    [WidgetSubType.OF_ATTENTE_VALIDATION]: OfEtat.EN_ATTENTE_VALIDATION,
    [WidgetSubType.OF_ATTENTE_PLANIF]: OfEtat.EN_ATTENTE_PLANNIF,
    // Legacy — n'est plus affiché mais conservé pour les configs existantes.
    [WidgetSubType.OF_ATTENTE_BILAN_MATIERE]: OfEtat.EN_ATTENTE_BILAN_MATIERE,
  };

  private countBySubType(subType: WidgetSubType): number {
    const etat = this.etatForSubType[subType];
    return this.data()?.ofs?.filter((o) => o.etat === etat).length ?? 0;
  }

  countPlanif = computed(() => this.countBySubType(WidgetSubType.OF_ATTENTE_PLANIF));
  countProd = computed(() => this.countBySubType(WidgetSubType.OF_ATTENTE_PROD));
  countEnProd = computed(() => this.countBySubType(WidgetSubType.OF_EN_PROD));
  countReinte = computed(() => this.countBySubType(WidgetSubType.OF_ATTENTE_REINTEGRATION));
  countValidation = computed(() => this.countBySubType(WidgetSubType.OF_ATTENTE_VALIDATION));

  ofs = computed(() => {
    if (!this.data() || !this.subType()) {
      return [];
    }
    const etat = this.etatForSubType[this.subType()];
    return this.data().ofs.filter((o) => o.etat === etat);
  });

  onSelect(fo: OF) {
    // Routage basé sur l'état réel de l'OF (pas le subType courant) — une OF
    // sélectionnée depuis le dialog "Voir tout" peut avoir n'importe quel état.
    if (fo.etat === OfEtat.EN_ATTENTE_REINTE_PDC) {
      this.router.navigate([`/pdc/reintegration`], { queryParams: { ofId: fo.id } });
      return;
    }
    // Tous les autres états en attente / en prod / validation passent par le
    // suivi opérationnel (voir of/CLAUDE.md, navigation depuis of-small-card).
    this.router.navigate([`/productions/suivi/ope`], { queryParams: { ofId: fo.id } });
  }

  articleInfosById(articleId: string) {
    return this.articlesInfosFacade.getByArticleId(articleId);
  }

  totalCount = computed(
    () =>
      (this.countPlanif() +
        this.countProd() +
        this.countEnProd() +
        this.countReinte() +
        this.countValidation()) as number,
  );

  /** Ouvre la dialog "Voir tout" avec stats, filtres, recherche et export Excel. */
  async onSeeAll(): Promise<void> {
    const ofs = this.data()?.ofs ?? [];
    const dialogData: OfListDialogData = {
      ofs,
      initialSubType: this.subType(),
    };
    const ref = this.dialogs.open(
      OfListDialogComponent,
      undefined,
      undefined,
      dialogData,
    );
    const selected = (await firstValueFrom(ref.afterClosed())) as OF | null;
    if (selected) this.onSelect(selected);
  }
}
